import { useState, useEffect } from "react";
import { JobValueTag, RevenueBanner, fmtMoney } from "@/components/JobValue";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { 
  Calendar, Clock, User, MapPin, Phone, MessageSquare, 
  ChevronLeft, ChevronRight, List, CalendarDays, X, Edit, Send, Bell
} from "lucide-react";
import { toast } from "sonner";
import BottomNav from "@/components/BottomNav";
import Header from "@/components/Header";
import JobDialog from "@/components/JobDialog";
import FlexibleScheduleBadge from "@/components/FlexibleScheduleBadge";
import { ScreenCheckBadge } from "@/components/ScreenCheckBadge";
import { JobTypeBadge } from "@/components/JobTypeBadge";
import { FrameCheckBadge } from "@/components/FrameCheckBadge";
import { getTechColor } from "@/utils/techColor";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { JobTimeline } from "@/components/JobTimeline";

const BACKEND_URL = window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function Jobs() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);
  const [user, setUser] = useState(location.state?.user || null);
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState({});
  const [techs, setTechs] = useState([]);
  const [filterType, setFilterType] = useState("today");
  const [selectedTech, setSelectedTech] = useState("All Techs");
  const [showJobDialog, setShowJobDialog] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [deleteJobId, setDeleteJobId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sendingDetails, setSendingDetails] = useState(false);
  const [sendingTechDetails, setSendingTechDetails] = useState(false);
  
  // View toggle: "list" or "calendar"
  const [viewMode, setViewMode] = useState("calendar");
  
  // Calendar state
  const [calendarView, setCalendarView] = useState("week"); // week or day
  const [currentDate, setCurrentDate] = useState(new Date());
  const [weekData, setWeekData] = useState(null);
  const [dayData, setDayData] = useState(null);
  const [selectedCalendarJob, setSelectedCalendarJob] = useState(null);

  const openEditJob = (job) => {
    setEditingJob(job);
    setShowJobDialog(true);
  };

  // Deep link: /jobs?job=<job_id> opens that job's dialog once jobs load.
  useEffect(() => {
    const wanted = new URLSearchParams(location.search).get("job");
    if (!wanted || !isAuthenticated) return;
    const open = (j) => {
      openEditJob(j);
      window.history.replaceState({}, document.title, "/jobs");
    };
    const j = jobs.find((x) => x.job_id === wanted);
    if (j) return open(j);
    // Not in the current filter (e.g. a future job while viewing Today) — fetch it directly.
    axios.get(`${API}/jobs/${wanted}`, { withCredentials: true, headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` } })
      .then((r) => open(r.data))
      .catch(() => window.history.replaceState({}, document.title, "/jobs"));
  }, [jobs, location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Don't check auth if there's a session_id in hash - let AuthCallback handle it
    if (window.location.hash?.includes('session_id=')) {
      return;
    }
    
    if (location.state?.user && location.state?.fromAuth) {
      setIsAuthenticated(true);
      return;
    }

    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('session_token');
        const cachedUser = localStorage.getItem('user');
        
        if (token && cachedUser) {
          const response = await axios.get(`${API}/auth/me`, { 
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
          navigate('/login');
        }
      } catch (error) {
        localStorage.removeItem('session_token');
        localStorage.removeItem('user');
        setIsAuthenticated(false);
        navigate('/login');
      }
    };
    
    checkAuth();
  }, [navigate, location.state]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchJobs();
      fetchCustomers();
      fetchTechs();
    }
  }, [isAuthenticated, filterType, selectedTech]);

  // Calendar data fetching
  useEffect(() => {
    if (isAuthenticated && viewMode === "calendar") {
      if (calendarView === "week") {
        fetchWeekData();
      } else {
        fetchDayData();
      }
    }
  }, [isAuthenticated, viewMode, calendarView, currentDate]);

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${API}/jobs`, {
        params: { filter_type: filterType, tech: selectedTech },
        withCredentials: true
      });
      const sortedJobs = response.data.sort((a, b) => {
        if (!a.time && !b.time) return 0;
        if (!a.time) return 1;
        if (!b.time) return -1;
        return a.time.localeCompare(b.time);
      });
      setJobs(sortedJobs);
    } catch (error) {
      toast.error("Failed to fetch jobs");
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`, { withCredentials: true });
      const customerMap = {};
      response.data.forEach(customer => {
        customerMap[customer.customer_id] = customer;
      });
      setCustomers(customerMap);
    } catch (error) {
      console.error("Failed to fetch customers");
    }
  };

  const fetchTechs = async () => {
    try {
      const response = await axios.get(`${API}/techs`, { withCredentials: true });
      setTechs(response.data);
    } catch (error) {
      console.error("Failed to fetch techs");
    }
  };

  const fetchWeekData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const dateStr = currentDate.toISOString().split('T')[0];
      const response = await axios.get(`${API}/calendar/week`, {
        params: { date: dateStr },
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      console.log('Week data received:', response.data);
      console.log('Sample job city:', response.data.jobs?.[0]?.city);
      setWeekData(response.data);
    } catch (error) {
      toast.error("Failed to fetch calendar data");
    } finally {
      setLoading(false);
    }
  };

  const fetchDayData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const dateStr = currentDate.toISOString().split('T')[0];
      const response = await axios.get(`${API}/calendar/day`, {
        params: { date: dateStr },
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setDayData(response.data);
    } catch (error) {
      toast.error("Failed to fetch calendar data");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusToggle = async (job) => {
    const statusCycle = ["Open", "Completed"];
    const currentIndex = statusCycle.indexOf(job.status);
    const nextStatus = statusCycle[(currentIndex + 1) % statusCycle.length];

    try {
      await axios.put(
        `${API}/jobs/${job.job_id}`,
        { status: nextStatus },
        { withCredentials: true }
      );
      toast.success(`Job marked as ${nextStatus}`);
      fetchJobs();
    } catch (error) {
      toast.error("Failed to update job status");
    }
  };

  const handleDeleteJob = async () => {
    if (!deleteJobId) return;

    try {
      await axios.delete(`${API}/jobs/${deleteJobId}`, { withCredentials: true });
      toast.success("Job deleted");
      setDeleteJobId(null);
      fetchJobs();
    } catch (error) {
      toast.error("Failed to delete job");
    }
  };

  const openMaps = (address) => {
    const encoded = encodeURIComponent(address);
    const ua = navigator.userAgent || "";
    if (/iPad|iPhone|iPod/.test(ua)) {
      // iOS: maps:// scheme triggers Apple Maps directly. The browser stays
      // on the current page (no new blank tab to navigate back from).
      window.location.href = `maps://?q=${encoded}`;
    } else if (/Android/i.test(ua)) {
      // Android: geo: scheme opens the user's default maps app.
      window.location.href = `geo:0,0?q=${encoded}`;
    } else {
      // Desktop: open Google Maps in a new tab.
      window.open(
        `https://www.google.com/maps/search/?api=1&query=${encoded}`,
        '_blank',
        'noopener,noreferrer'
      );
    }
  };

  const openPhone = (phone) => {
    window.location.href = `tel:${phone}`;
  };

  const openSMS = (phone) => {
    window.location.href = `sms:${phone}`;
  };

  const sendAppointmentDetails = async (jobId) => {
    setSendingDetails(true);
    try {
      const token = localStorage.getItem('session_token');
      await axios.post(`${API}/jobs/${jobId}/send-appointment-details`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Appointment details sent to customer");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send appointment details");
    } finally {
      setSendingDetails(false);
    }
  };

  const sendTechDetails = async (jobId) => {
    setSendingTechDetails(true);
    try {
      const token = localStorage.getItem('session_token');
      await axios.post(`${API}/jobs/${jobId}/send-tech-details`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Job details sent to tech");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send job details to tech");
    } finally {
      setSendingTechDetails(false);
    }
  };

  const formatTime12Hour = (time24) => {
    if (!time24) return "";
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Open":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "Completed":
        return "bg-green-100 text-green-800 border-green-200";
      case "Needs to be Scheduled":
        return "bg-amber-100 text-amber-800 border-amber-200";
      case "Pending Confirmation":
        return "bg-purple-100 text-purple-800 border-purple-200";
      default:
        return "bg-zinc-100 text-zinc-800 border-zinc-200";
    }
  };

  // Calendar helpers
  const navigatePrevious = () => {
    if (calendarView === "week") {
      setCurrentDate(new Date(currentDate.getTime() - 7 * 24 * 60 * 60 * 1000));
    } else {
      setCurrentDate(new Date(currentDate.getTime() - 24 * 60 * 60 * 1000));
    }
  };

  const navigateNext = () => {
    if (calendarView === "week") {
      setCurrentDate(new Date(currentDate.getTime() + 7 * 24 * 60 * 60 * 1000));
    } else {
      setCurrentDate(new Date(currentDate.getTime() + 24 * 60 * 60 * 1000));
    }
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const getWeekDays = () => {
    if (!weekData) return [];
    const [year, month, day] = weekData.week_start.split('-').map(Number);
    const start = new Date(year, month - 1, day);
    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      days.push(d);
    }
    
    // Reorder days to start from current day (today at top)
    const today = new Date();
    const todayDayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const mondayBasedToday = todayDayOfWeek === 0 ? 6 : todayDayOfWeek - 1; // Convert to Monday=0 based
    
    // Only reorder if we're viewing the current week
    const weekStartDate = new Date(year, month - 1, day);
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const isCurrentWeek = days.some(d => {
      const dStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      return dStr === todayStr;
    });
    
    if (isCurrentWeek) {
      // Rotate array to put today first
      const rotated = [...days.slice(mondayBasedToday), ...days.slice(0, mondayBasedToday)];
      return rotated;
    }
    
    return days;
  };

  const getJobsForDay = (dateStr) => {
    if (!weekData) return [];
    return weekData.jobs.filter(job => job.date === dateStr && job.status !== "Cancelled" && job.status !== "Needs to be Scheduled");
  };

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-zinc-200 border-t-[#207024]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header user={user} />
      
      <div className={`${viewMode === "calendar" ? "max-w-6xl" : "max-w-md"} mx-auto pb-24 p-4`}>
        <div className="mb-6" data-testid="jobs-header">
          <div className="flex items-center justify-between mb-4">
            <h1 className="font-barlow text-4xl font-bold text-zinc-950 uppercase">Jobs</h1>
            
            {/* View Toggle */}
            <div className="flex bg-zinc-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode("list")}
                data-testid="view-list-btn"
                className={`px-3 py-2 rounded-md text-sm font-semibold flex items-center gap-1 transition-colors ${
                  viewMode === "list" ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-500"
                }`}
              >
                <List className="w-4 h-4" />
                List
              </button>
              <button
                onClick={() => setViewMode("calendar")}
                data-testid="view-calendar-btn"
                className={`px-3 py-2 rounded-md text-sm font-semibold flex items-center gap-1 transition-colors ${
                  viewMode === "calendar" ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-500"
                }`}
              >
                <CalendarDays className="w-4 h-4" />
                Calendar
              </button>
            </div>
          </div>

          {/* List View Controls */}
          {viewMode === "list" && (
            <>
              <div className="flex gap-2 mb-4" data-testid="filter-tabs">
                <button
                  onClick={() => setFilterType("today")}
                  data-testid="filter-today"
                  className={`flex-1 h-11 rounded-lg font-semibold transition-colors ${
                    filterType === "today"
                      ? "bg-[#207024] text-white"
                      : "bg-white border-2 border-zinc-200 text-zinc-900 hover:bg-zinc-50"
                  }`}
                >
                  Today
                </button>
                <button
                  onClick={() => setFilterType("all")}
                  data-testid="filter-all"
                  className={`flex-1 h-11 rounded-lg font-semibold transition-colors ${
                    filterType === "all"
                      ? "bg-[#207024] text-white"
                      : "bg-white border-2 border-zinc-200 text-zinc-900 hover:bg-zinc-50"
                  }`}
                >
                  All Jobs
                </button>
                <button
                  onClick={() => setFilterType("unscheduled")}
                  data-testid="filter-unscheduled"
                  className={`flex-1 h-11 rounded-lg font-semibold transition-colors ${
                    filterType === "unscheduled"
                      ? "bg-[#207024] text-white"
                      : "bg-white border-2 border-zinc-200 text-zinc-900 hover:bg-zinc-50"
                  }`}
                >
                  Unscheduled
                </button>
              </div>

              {user?.role === "Admin" && (
                <select
                  value={selectedTech}
                  onChange={(e) => setSelectedTech(e.target.value)}
                  data-testid="tech-filter-dropdown"
                  className="w-full h-12 rounded-lg border-2 border-zinc-200 focus:border-[#207024] text-base px-4 bg-white font-inter"
                >
                  <option>All Techs</option>
                  {techs.map((tech) => (
                    <option key={tech.tech_id} value={tech.name}>
                      {tech.name}
                    </option>
                  ))}
                </select>
              )}
            </>
          )}

          {/* Calendar View Controls */}
          {viewMode === "calendar" && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <button
                  onClick={navigatePrevious}
                  data-testid="nav-previous"
                  className="h-11 w-11 rounded-lg bg-white border-2 border-zinc-200 text-zinc-900 hover:bg-zinc-50 flex items-center justify-center"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>

                <button
                  onClick={goToToday}
                  data-testid="go-to-today"
                  className="h-11 px-4 rounded-lg bg-[#207024] hover:bg-[#1a5c1d] text-white font-semibold"
                >
                  Today
                </button>

                <button
                  onClick={navigateNext}
                  data-testid="nav-next"
                  className="h-11 w-11 rounded-lg bg-white border-2 border-zinc-200 text-zinc-900 hover:bg-zinc-50 flex items-center justify-center"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>

                <div className="flex-1 text-center">
                  <p className="font-inter text-lg font-bold text-zinc-900">
                    {calendarView === "week" && weekData && (
                      `${new Date(weekData.week_start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}-${new Date(weekData.week_end).toLocaleDateString('en-US', { day: 'numeric' })}, ${new Date(weekData.week_end).getFullYear()}`
                    )}
                    {calendarView === "day" && currentDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                  </p>
                </div>

                <button
                  onClick={() => setCalendarView(calendarView === "week" ? "day" : "week")}
                  data-testid="toggle-calendar-view"
                  className="h-11 px-4 rounded-lg bg-white border-2 border-zinc-200 text-zinc-900 font-semibold hover:bg-zinc-50 flex items-center gap-2"
                >
                  {calendarView === "week" ? "Day" : "Week"}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* List View Content */}
        {viewMode === "list" && (
          <div className="space-y-4" data-testid="jobs-list">
            {jobs.length === 0 ? (
              <div className="text-center py-12">
                <p className="font-inter text-zinc-500">No jobs found</p>
              </div>
            ) : (
              jobs.map((job) => {
                const customer = customers[job.customer_id];
                const isCompleted = job.status === "Completed";

                return (
                  <div
                    key={job.job_id}
                    data-testid={`job-card-${job.job_id}`}
                    className={`bg-white rounded-xl border-2 border-zinc-100 shadow-sm p-5 hover:border-[#207024]/30 transition-colors ${
                      isCompleted ? "opacity-60" : ""
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-inter text-lg font-bold text-zinc-900" data-testid="job-customer-name">
                            {customer?.fullName || "Unknown Customer"}
                          </h3>
                          {customer?.is_repeat && (
                            <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 text-[10px] font-semibold rounded-full">
                              Repeat
                            </span>
                          )}
                          <FlexibleScheduleBadge flexible={job.flexible_schedule} size="sm" />
                          <JobTypeBadge jobType={job.job_type} size="sm" />
                          <FrameCheckBadge count={job.frame_check_count} />
                        </div>
                        <div className="flex items-center gap-2 mt-1 text-sm text-zinc-600">
                          <User className="w-4 h-4" />
                          <span data-testid="job-assigned-tech">
                            {job.assignedEmployee}
                            {job.assignedEmployee2 && ` + ${job.assignedEmployee2}`}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => handleStatusToggle(job)}
                        data-testid="job-status-badge"
                        className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider border ${getStatusColor(
                          job.status
                        )}`}
                      >
                        {job.status}
                      </button>
                    </div>

                    <div className="space-y-2 mb-4">
                      {job.date && (
                        <div className="flex items-center gap-2 text-sm text-zinc-700" data-testid="job-date-time">
                          <Calendar className="w-4 h-4 text-[#207024]" />
                          <span>{job.date}</span>
                          {job.time && (
                            <>
                              <Clock className="w-4 h-4 text-[#207024] ml-2" />
                              <span>{formatTime12Hour(job.time)} ({job.duration}h)</span>
                            </>
                          )}
                        </div>
                      )}
                      {customer?.address && (
                        <div className="flex items-center gap-2 text-sm text-zinc-700">
                          <MapPin className="w-4 h-4 text-[#207024]" />
                          <span className="flex-1" data-testid="job-address">{customer.address}</span>
                        </div>
                      )}
                      {job.notes && (
                        <div className="text-sm text-zinc-600 italic bg-zinc-50 rounded-lg p-2 mt-1">
                          {job.notes}
                        </div>
                      )}
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => customer?.address && openMaps(customer.address)}
                        data-testid="job-map-button"
                        className="h-11 px-4 rounded-lg bg-zinc-100 hover:bg-zinc-200 text-[#207024] font-semibold flex items-center gap-2 border border-zinc-200 flex-1"
                      >
                        <MapPin className="w-5 h-5" />
                        Map
                      </button>
                      <button
                        onClick={() => customer?.phoneNumber && openPhone(customer.phoneNumber)}
                        data-testid="job-call-button"
                        className="h-11 px-4 rounded-lg bg-zinc-100 hover:bg-zinc-200 text-[#207024] font-semibold flex items-center gap-2 border border-zinc-200 flex-1"
                      >
                        <Phone className="w-5 h-5" />
                        Call
                      </button>
                      <button
                        onClick={() => customer?.phoneNumber && openSMS(customer.phoneNumber)}
                        data-testid="job-text-button"
                        className="h-11 px-4 rounded-lg bg-zinc-100 hover:bg-zinc-200 text-[#207024] font-semibold flex items-center gap-2 border border-zinc-200 flex-1"
                      >
                        <MessageSquare className="w-5 h-5" />
                        Text
                      </button>
                    </div>

                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={() => {
                          setEditingJob(job);
                          setShowJobDialog(true);
                        }}
                        data-testid="edit-job-button"
                        className="h-10 flex-1 rounded-lg bg-white border-2 border-zinc-200 text-zinc-900 font-semibold hover:bg-zinc-50"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => setDeleteJobId(job.job_id)}
                        data-testid="delete-job-button"
                        className="h-10 flex-1 rounded-lg bg-red-50 border-2 border-red-200 text-red-700 font-semibold hover:bg-red-100"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Calendar View Content */}
        {viewMode === "calendar" && (
          <>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-zinc-200 border-t-[#207024]"></div>
              </div>
            ) : (
              <>
                {calendarView === "week" && weekData && (
                  <>
                    {/* Week Total Screen Count (Admin only) */}
                    {user?.role === "Admin" && (
                      <RevenueBanner
                        label="Week" testid="week-revenue-banner"
                        screens={weekData.week_screen_total} completedScreens={weekData.week_completed_screens}
                        value={weekData.week_value_total} earned={weekData.week_value_completed}
                      />
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-7 gap-4" data-testid="week-view">
                    {getWeekDays().map((day, index) => {
                      const dateStr = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
                      const dayJobs = getJobsForDay(dateStr);
                      const today = new Date();
                      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
                      const isToday = dateStr === todayStr;
                      const dayScreenCount = weekData.daily_screen_counts?.[dateStr] || 0;
                      const dayValue = weekData.daily_value_totals?.[dateStr] || 0;

                      return (
                        <div key={index} className={`rounded-xl border-2 ${isToday ? 'border-[#207024] bg-green-50' : 'border-zinc-100'} p-4`}>
                          <div className="mb-3">
                            <div className="flex justify-between items-center">
                              {/* Day-of-week + date on ONE line to save
                                  vertical space in the mobile week view. */}
                              <p className="font-inter text-lg font-bold text-zinc-900">
                                <span className="font-barlow text-sm font-bold uppercase text-zinc-500 mr-2">
                                  {day.toLocaleDateString('en-US', { weekday: 'short' })}
                                </span>
                                {day.getDate()}
                              </p>
                              {/* Daily Screen Count (Admin only) */}
                              {user?.role === "Admin" && (dayScreenCount > 0 || dayValue > 0) && (
                                <div className="flex items-center gap-1">
                                  {dayValue > 0 && (
                                    <span className="text-xs font-bold text-white bg-[#207024] px-2 py-1 rounded" data-testid={`day-value-${dateStr}`}>
                                      {fmtMoney(dayValue)}
                                    </span>
                                  )}
                                  {dayScreenCount > 0 && (
                                    <span className="text-xs font-semibold text-[#207024] bg-[#207024]/10 px-2 py-1 rounded">
                                      {dayScreenCount} screens
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="space-y-2">
                            {dayJobs.length === 0 ? (
                              <p className="text-sm text-zinc-400 italic">No jobs</p>
                            ) : (
                              dayJobs.map((job) => {
                                const isReminder = (job.job_type || "").toLowerCase() === "reminder";
                                if (isReminder) {
                                  return (
                                    <div
                                      key={job.job_id}
                                      onClick={(e) => { e.stopPropagation(); openEditJob(job); }}
                                      data-testid={`reminder-card-${job.job_id}`}
                                      className="bg-amber-50 rounded-lg border border-amber-300 border-l-4 border-l-amber-500 p-2 text-sm hover:border-amber-500 cursor-pointer transition-colors"
                                    >
                                      <div className="flex items-start gap-1 min-w-0">
                                        <Bell className="w-3.5 h-3.5 mt-0.5 text-amber-600 flex-shrink-0" />
                                        <p className="font-semibold text-amber-900 truncate flex-1">
                                          {job.customer?.fullName || job.title || "Reminder"}
                                        </p>
                                        {job.time && (
                                          <span className="text-[10px] text-amber-800 shrink-0">
                                            {formatTime12Hour(job.time)}
                                          </span>
                                        )}
                                      </div>
                                      {job.title && job.customer?.fullName && (
                                        <p className="text-[11px] text-amber-800 ml-4 italic truncate">
                                          {job.title}
                                        </p>
                                      )}
                                      <div className="ml-4 flex items-center gap-1 mt-0.5">
                                        <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${getStatusColor(job.status)}`}>
                                          {job.status}
                                        </span>
                                        {job.assignedEmployee && (
                                          <span className="text-[10px] text-amber-700">
                                            {job.assignedEmployee}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  );
                                }
                                const techColor = getTechColor(job.assignedEmployee, techs);
                                const tech2Color = job.assignedEmployee2 ? getTechColor(job.assignedEmployee2, techs) : null;
                                return (
                                <div
                                  key={job.job_id}
                                  className="bg-white rounded-lg border border-zinc-200 border-l-4 p-2 text-sm hover:border-[#207024] cursor-pointer transition-colors"
                                  style={{ borderLeftColor: techColor.bg }}
                                  onClick={() => setSelectedCalendarJob(job)}
                                >
                                  {/* TOP ROW: name + time on the left,
                                      status + screen count on the right. */}
                                  <div className="flex items-start gap-2 min-w-0">
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-baseline gap-2 min-w-0">
                                        <p className="font-semibold text-zinc-900 truncate">{job.customer?.fullName || "Unknown"}</p>
                                        <span className="text-xs text-zinc-500 shrink-0">{formatTime12Hour(job.time)}</span>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                      <FlexibleScheduleBadge flexible={job.flexible_schedule} size="xs" />
                                      <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${getStatusColor(job.status)}`}>
                                        {job.status}
                                      </span>
                                      {user?.role === "Admin" && job.screen_count > 0 && (
                                        <span className="text-[10px] font-medium text-zinc-600 whitespace-nowrap">
                                          {job.screen_count}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  {/* Address */}
                                  {job.customer?.address && job.customer.address !== "Not provided yet" && (
                                    <p className="text-[10px] text-zinc-400 truncate">{job.customer.address}</p>
                                  )}
                                  {/* Tech pill(s) + job-type badge + screen-check pill — color-coded */}
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    <JobTypeBadge jobType={job.job_type} size="xs" />
                                    <FrameCheckBadge count={job.frame_check_count} size="xs" />
                                    {user?.role === "Admin" && <JobValueTag job={job} />}
                                    {job.screen_check_preference && job.screen_check_preference !== "selected_only" && (
                                      <ScreenCheckBadge preference={job.screen_check_preference} size="xs" />
                                    )}
                                    {job.assignedEmployee && (
                                      <span
                                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold"
                                        style={{ backgroundColor: techColor.bg, color: techColor.fg }}
                                      >
                                        {job.assignedEmployee}
                                      </span>
                                    )}
                                    {job.assignedEmployee2 && tech2Color && (
                                      <span
                                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold"
                                        style={{ backgroundColor: tech2Color.bg, color: tech2Color.fg }}
                                      >
                                        {job.assignedEmployee2}
                                      </span>
                                    )}
                                  </div>
                                  {job.notes && (
                                    <p className="text-xs text-zinc-500 italic mt-1 truncate">{job.notes}</p>
                                  )}
                                </div>
                                );
                              })
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  </>
                )}

                {calendarView === "day" && dayData && (
                  <>
                    {/* Day Total Screen Count (Admin only) */}
                    {user?.role === "Admin" && (
                      <RevenueBanner
                        label="Day" testid="day-revenue-banner"
                        screens={dayData.day_screen_total}
                        value={dayData.day_value_total} earned={dayData.day_value_completed}
                      />
                    )}
                    <div className="space-y-4" data-testid="day-view">
                    {dayData.jobs.length === 0 ? (
                      <div className="text-center py-12">
                        <p className="font-inter text-zinc-500">No jobs scheduled for this day</p>
                      </div>
                    ) : (
                      dayData.jobs.map((job) => {
                        const isReminder = (job.job_type || "").toLowerCase() === "reminder";
                        if (isReminder) {
                          return (
                            <div
                              key={job.job_id}
                              onClick={() => openEditJob(job)}
                              data-testid={`reminder-card-day-${job.job_id}`}
                              className="bg-amber-50 rounded-xl border-2 border-amber-300 shadow-sm p-4 hover:border-amber-500 cursor-pointer transition-colors"
                            >
                              <div className="flex items-start gap-3">
                                <div className="mt-0.5 h-9 w-9 rounded-full bg-amber-400 text-white flex items-center justify-center flex-shrink-0">
                                  <Bell className="w-4 h-4" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <h3 className="font-inter text-lg font-bold text-amber-900">
                                      {job.customer?.fullName || job.title || "Reminder"}
                                    </h3>
                                    <JobTypeBadge jobType="reminder" size="sm" />
                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${getStatusColor(job.status)}`}>
                                      {job.status}
                                    </span>
                                  </div>
                                  {job.title && job.customer?.fullName && (
                                    <p className="text-sm font-semibold text-amber-900 mt-1">
                                      {job.title}
                                    </p>
                                  )}
                                  <div className="text-sm text-amber-800 mt-1 flex flex-wrap gap-x-4">
                                    {job.time && <span>{formatTime12Hour(job.time)}</span>}
                                    {job.assignedEmployee && <span>{job.assignedEmployee}</span>}
                                  </div>
                                  {job.notes && (
                                    <p className="text-sm text-amber-800 mt-2 italic">{job.notes}</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        }
                        const techColor = getTechColor(job.assignedEmployee, techs);
                        const tech2Color = job.assignedEmployee2 ? getTechColor(job.assignedEmployee2, techs) : null;
                        return (
                        <div
                          key={job.job_id}
                          className="bg-white rounded-xl border-2 border-l-4 border-zinc-100 shadow-sm p-5 hover:border-[#207024]/30 cursor-pointer transition-colors"
                          style={{ borderLeftColor: techColor.bg }}
                          onClick={() => setSelectedCalendarJob(job)}
                        >
                          <div className="flex items-start justify-between mb-3 gap-3">
                            <div className="flex-1 min-w-0">
                              {/* Customer + time on ONE line. */}
                              <div className="flex items-baseline gap-2 flex-wrap">
                                <h3 className="font-inter text-lg font-bold text-zinc-900">
                                  {job.customer?.fullName || "Unknown Customer"}
                                </h3>
                                <span className="text-sm font-medium text-zinc-600">
                                  {formatTime12Hour(job.time)}
                                </span>
                                <FlexibleScheduleBadge flexible={job.flexible_schedule} size="sm" />
                                <JobTypeBadge jobType={job.job_type} size="sm" />
                                <FrameCheckBadge count={job.frame_check_count} />
                              </div>
                              <p className="text-sm text-zinc-600 mt-1">{job.customer?.address || "No address"}</p>
                            </div>
                            {/* Status + screen count in the top-right,
                                same visual line as the customer name. */}
                            <div className="flex flex-col items-end gap-1 shrink-0">
                              <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider border ${getStatusColor(job.status)}`}>
                                {job.status}
                              </span>
                              {user?.role === "Admin" && <JobValueTag job={job} size="sm" />}
                              {user?.role === "Admin" && job.screen_count > 0 && (
                                <span className="text-xs font-medium text-zinc-500">
                                  {job.screen_count} screens
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-zinc-500 font-semibold">Duration</p>
                              <p className="text-zinc-900">{job.duration ? `${job.duration}h` : "Not set"}</p>
                            </div>
                            <div>
                              <p className="text-zinc-500 font-semibold">Phone</p>
                              <p className="text-zinc-900">{job.customer?.phoneNumber || "N/A"}</p>
                            </div>
                            <div className="col-span-2">
                              <p className="text-zinc-500 font-semibold mb-1">Assigned Tech(s)</p>
                              <div className="flex flex-wrap gap-1.5">
                                {job.assignedEmployee ? (
                                  <span
                                    className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold"
                                    style={{ backgroundColor: techColor.bg, color: techColor.fg }}
                                  >
                                    {job.assignedEmployee}
                                  </span>
                                ) : (
                                  <span className="text-zinc-400 italic text-xs">Not assigned</span>
                                )}
                                {job.assignedEmployee2 && tech2Color && (
                                  <span
                                    className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold"
                                    style={{ backgroundColor: tech2Color.bg, color: tech2Color.fg }}
                                  >
                                    {job.assignedEmployee2}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          {(job.notes || (job.screen_check_preference && job.screen_check_preference !== "selected_only")) && (
                            <div className="mt-3 pt-3 border-t border-zinc-100 space-y-2">
                              {/* Screen Check appears in the Notes / Scope
                                  of Work area so techs reading the job list
                                  see it alongside any free-form notes. */}
                              {job.screen_check_preference && job.screen_check_preference !== "selected_only" && (
                                <ScreenCheckBadge preference={job.screen_check_preference} size="md" />
                              )}
                              {job.notes && (
                                <p className="text-sm text-zinc-600 italic">{job.notes}</p>
                              )}
                            </div>
                          )}
                        </div>
                        );
                      })
                    )}
                  </div>
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>

      <JobDialog
        open={showJobDialog}
        onOpenChange={setShowJobDialog}
        job={editingJob}
        isAdmin={user?.role === "Admin"}
        onSuccess={() => {
          fetchJobs();
          setShowJobDialog(false);
          setEditingJob(null);
          if (calendarView === "week") fetchWeekData(); else fetchDayData();
        }}
      />

      <AlertDialog open={!!deleteJobId} onOpenChange={(open) => !open && setDeleteJobId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Job</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this job? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-job">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteJob} data-testid="confirm-delete-job" className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Calendar Job Detail Dialog */}
      <Dialog open={!!selectedCalendarJob} onOpenChange={(open) => !open && setSelectedCalendarJob(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {selectedCalendarJob?.customer?.fullName || "Job Details"}
            </DialogTitle>
          </DialogHeader>
          
          {selectedCalendarJob && (
            <div className="space-y-4">
              {/* Status badge - clickable to change */}
              <div className="flex items-center gap-3">
                <span className="text-sm text-zinc-500">Status:</span>
                <div className="relative">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const dropdown = e.currentTarget.nextElementSibling;
                      dropdown.classList.toggle('hidden');
                    }}
                    className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider border ${getStatusColor(selectedCalendarJob.status)}`}
                  >
                    {(selectedCalendarJob.status || 'scheduled').replace('_', ' ')}
                  </button>
                  <div className="hidden absolute top-full left-0 mt-1 bg-white border rounded-lg shadow-lg z-10 min-w-[140px]">
                    {['Open', 'Completed', 'Needs to be Scheduled'].map((status) => (
                      <button
                        key={status}
                        onClick={async (e) => {
                          e.stopPropagation();
                          e.currentTarget.parentElement.classList.add('hidden');
                          if (status === selectedCalendarJob.status) return;
                          // Confirm destructive change — this wipes date/time
                          // and bit us in prod (Alice Sanderson, lead_4c3b8f9fa024)
                          // when an admin tap-misfired on this dropdown.
                          if (status === 'Needs to be Scheduled') {
                            const hasSchedule = selectedCalendarJob.date || selectedCalendarJob.time;
                            if (hasSchedule) {
                              const ok = window.confirm(
                                `This will CLEAR the appointment date (${selectedCalendarJob.date || "—"}) and time (${selectedCalendarJob.time || "—"}) and remove this job from the calendar.\n\nContinue?`
                              );
                              if (!ok) return;
                            }
                          }
                          try {
                            const token = localStorage.getItem('session_token');
                            const updateData = { status };
                            // Clear date/time when marking as "Needs to be Scheduled"
                            if (status === 'Needs to be Scheduled') {
                              updateData.date = null;
                              updateData.time = null;
                            }
                            await axios.put(
                              `${API}/jobs/${selectedCalendarJob.job_id}/status`,
                              updateData,
                              { headers: { Authorization: `Bearer ${token}` } }
                            );
                            toast.success(`Job marked as ${status}`);
                            setSelectedCalendarJob(null);
                            fetchJobs();
                            fetchWeekData();
                          } catch (error) {
                            toast.error(error.response?.data?.detail || "Failed to update status");
                          }
                        }}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-zinc-100 first:rounded-t-lg last:rounded-b-lg ${
                          status === selectedCalendarJob.status ? 'bg-zinc-100 font-semibold' : ''
                        }`}
                      >
                        {status}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Job details */}
              <div className="space-y-3">
                {selectedCalendarJob.date && (
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="w-4 h-4 text-[#207024]" />
                    <span>{selectedCalendarJob.date}</span>
                    {selectedCalendarJob.time && (
                      <>
                        <Clock className="w-4 h-4 text-[#207024] ml-2" />
                        <span>{formatTime12Hour(selectedCalendarJob.time)}</span>
                      </>
                    )}
                  </div>
                )}
                
                {selectedCalendarJob.customer?.address && (
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="w-4 h-4 text-[#207024]" />
                    <span>{selectedCalendarJob.customer.address}</span>
                  </div>
                )}
                
                {selectedCalendarJob.assignedEmployee && (
                  <div className="flex items-center gap-2 text-sm">
                    <User className="w-4 h-4 text-[#207024]" />
                    <span>
                      {selectedCalendarJob.assignedEmployee}
                      {selectedCalendarJob.assignedEmployee2 && ` + ${selectedCalendarJob.assignedEmployee2}`}
                    </span>
                  </div>
                )}
                
                {selectedCalendarJob.customer?.phoneNumber && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="w-4 h-4 text-[#207024]" />
                    <span>{selectedCalendarJob.customer.phoneNumber}</span>
                  </div>
                )}
                
                {/* Job Notes */}
                {selectedCalendarJob.notes && (
                  <div className="text-sm text-zinc-600 italic bg-zinc-50 rounded-lg p-3 mt-2">
                    {selectedCalendarJob.notes}
                  </div>
                )}
              </div>
              
              {/* Action buttons */}
              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => {
                    if (selectedCalendarJob.customer?.address) {
                      openMaps(selectedCalendarJob.customer.address);
                    }
                  }}
                  disabled={!selectedCalendarJob.customer?.address}
                  className="h-11 px-4 rounded-lg bg-zinc-100 hover:bg-zinc-200 text-[#207024] font-semibold flex items-center gap-2 border border-zinc-200 flex-1 justify-center disabled:opacity-50"
                >
                  <MapPin className="w-5 h-5" />
                  Map
                </button>
                <button
                  onClick={() => {
                    if (selectedCalendarJob.customer?.phoneNumber) {
                      openPhone(selectedCalendarJob.customer.phoneNumber);
                    }
                  }}
                  disabled={!selectedCalendarJob.customer?.phoneNumber}
                  className="h-11 px-4 rounded-lg bg-zinc-100 hover:bg-zinc-200 text-[#207024] font-semibold flex items-center gap-2 border border-zinc-200 flex-1 justify-center disabled:opacity-50"
                >
                  <Phone className="w-5 h-5" />
                  Call
                </button>
                <button
                  onClick={() => {
                    if (selectedCalendarJob.customer_id) {
                      setSelectedCalendarJob(null);
                      navigate('/messages', { state: { customerId: selectedCalendarJob.customer_id } });
                    }
                  }}
                  disabled={!selectedCalendarJob.customer_id}
                  className="h-11 px-4 rounded-lg bg-zinc-100 hover:bg-zinc-200 text-[#207024] font-semibold flex items-center gap-2 border border-zinc-200 flex-1 justify-center disabled:opacity-50"
                >
                  <MessageSquare className="w-5 h-5" />
                  Message
                </button>
              </div>
              
              {/* Edit Job button */}
              <button
                onClick={() => {
                  // First try to find from the jobs list (full job data)
                  let jobToEdit = jobs.find(j => j.job_id === selectedCalendarJob.job_id);
                  
                  // If not found in jobs list, use the selectedCalendarJob itself
                  // (calendar jobs may have customer embedded)
                  if (!jobToEdit) {
                    jobToEdit = selectedCalendarJob;
                  }
                  
                  setSelectedCalendarJob(null);
                  setEditingJob(jobToEdit);
                  setShowJobDialog(true);
                }}
                className="w-full h-11 rounded-lg bg-[#207024] hover:bg-[#1a5c1d] text-white font-semibold flex items-center gap-2 justify-center"
              >
                <Edit className="w-5 h-5" />
                Edit Job
              </button>

              {/* Text Appointment Details button */}
              {selectedCalendarJob.date && selectedCalendarJob.time && selectedCalendarJob.customer?.phoneNumber && (
                <button
                  onClick={() => sendAppointmentDetails(selectedCalendarJob.job_id)}
                  disabled={sendingDetails}
                  className="w-full h-11 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold flex items-center gap-2 justify-center disabled:opacity-50"
                >
                  <Send className="w-5 h-5" />
                  {sendingDetails ? "Sending..." : "Text Customer"}
                </button>
              )}

              {/* Text Tech Details button */}
              {selectedCalendarJob.date && selectedCalendarJob.assignedEmployee && (
                <button
                  onClick={() => sendTechDetails(selectedCalendarJob.job_id)}
                  disabled={sendingTechDetails}
                  className="w-full h-11 rounded-lg bg-orange-500 hover:bg-orange-600 text-white font-semibold flex items-center gap-2 justify-center disabled:opacity-50"
                >
                  <Send className="w-5 h-5" />
                  {sendingTechDetails ? "Sending..." : "Text Tech"}
                </button>
              )}
              <JobTimeline key={selectedCalendarJob.job_id} jobId={selectedCalendarJob.job_id} />
            </div>
          )}
        </DialogContent>
      </Dialog>

      <BottomNav />
    </div>
  );
}
